import { mockProducts } from "../data/mockProducts";
import { isSupabaseConfigured, supabase } from "./supabase";
import { getFunctionsErrorDetails } from "./functionErrors";

export const STOCK_STATUS_OPTIONS = [
  { value: "in_stock", label: "In Stock" },
  { value: "out_of_stock", label: "Out of Stock" },
  { value: "sold_out", label: "Sold Out" },
];

function normalizeProduct(product, index = 0) {
  return {
    id: product.id || `product-${index}`,
    name: product.name || "Untitled Product",
    price: Number(product.price || 0),
    category: product.category || "Signature",
    description: product.description || "",
    image_url: product.image_url || "",
    featured: Boolean(product.featured),
    stock_status: STOCK_STATUS_OPTIONS.some((option) => option.value === product.stock_status)
      ? product.stock_status
      : "in_stock",
    created_at: product.created_at || null,
    updated_at: product.updated_at || null,
  };
}

function sortProducts(products) {
  return [...products].sort((a, b) => {
    if (a.featured !== b.featured) return Number(b.featured) - Number(a.featured);
    if (a.created_at && b.created_at) return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    return a.name.localeCompare(b.name);
  });
}

function orderStorefrontProducts(products) {
  const normalizedProducts = [...products];
  const afganIndex = normalizedProducts.findIndex(
    (product) => String(product.name || "").trim().toLowerCase() === "afgan 9pm"
  );

  if (afganIndex === -1) {
    return normalizedProducts.reverse();
  }

  const [afganProduct] = normalizedProducts.splice(afganIndex, 1);
  return [afganProduct, ...normalizedProducts.reverse()];
}

export const productsService = {
  async fetchStorefrontProducts() {
    if (!isSupabaseConfigured) {
      return orderStorefrontProducts(mockProducts.map(normalizeProduct));
    }

    try {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, price, category, description, image_url, featured, stock_status, created_at, updated_at")
        .order("created_at", { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) {
        return orderStorefrontProducts(mockProducts.map(normalizeProduct));
      }

      return orderStorefrontProducts(data.map(normalizeProduct));
    } catch {
      return orderStorefrontProducts(mockProducts.map(normalizeProduct));
    }
  },

  async fetchAdminProducts(accessCode) {
    if (!isSupabaseConfigured) {
      throw new Error("Admin products are not configured yet. Add your Supabase settings to .env.");
    }

    const { data, error } = await supabase.functions.invoke("admin-products", {
      body: { accessCode, action: "list" },
    });

    if (error) {
      const details = await getFunctionsErrorDetails(error, "Failed to load products.");
      throw new Error(details);
    }
    if (!data?.success) {
      throw new Error(data?.message || "Failed to load products.");
    }

    return sortProducts((data.products || []).map(normalizeProduct));
  },

  async createAdminProduct(accessCode, product) {
    if (!isSupabaseConfigured) {
      throw new Error("Admin products are not configured yet. Add your Supabase settings to .env.");
    }

    const { data, error } = await supabase.functions.invoke("admin-products", {
      body: { accessCode, action: "create", product },
    });

    if (error) {
      const details = await getFunctionsErrorDetails(error, "Failed to create product.");
      throw new Error(details);
    }
    if (!data?.success) {
      throw new Error(data?.message || "Failed to create product.");
    }

    return normalizeProduct(data.product);
  },

  async updateAdminProduct(accessCode, productId, product) {
    if (!isSupabaseConfigured) {
      throw new Error("Admin products are not configured yet. Add your Supabase settings to .env.");
    }

    const { data, error } = await supabase.functions.invoke("admin-products", {
      body: { accessCode, action: "update", productId, product },
    });

    if (error) {
      const details = await getFunctionsErrorDetails(error, "Failed to update product.");
      throw new Error(details);
    }
    if (!data?.success) {
      throw new Error(data?.message || "Failed to update product.");
    }

    return normalizeProduct(data.product);
  },

  async deleteAdminProduct(accessCode, productId) {
    if (!isSupabaseConfigured) {
      throw new Error("Admin products are not configured yet. Add your Supabase settings to .env.");
    }

    const { data, error } = await supabase.functions.invoke("admin-products", {
      body: { accessCode, action: "delete", productId },
    });

    if (error) {
      const details = await getFunctionsErrorDetails(error, "Failed to delete product.");
      throw new Error(details);
    }
    if (!data?.success) {
      throw new Error(data?.message || "Failed to delete product.");
    }

    return true;
  },

  subscribeToProducts(onChange) {
    if (!isSupabaseConfigured) return null;

    return supabase
      .channel("public:products")
      .on("postgres_changes", { event: "*", schema: "public", table: "products" }, onChange)
      .subscribe();
  },
};
